import { UncontrolledTooltip } from 'reactstrap';

// COM-547: what each flag does; "vs" notes call out look-alike flags.
export const FEATURE_DESCRIPTIONS = {
  // Backend (Admin.py FEATURES)
  minimal_ui: 'Chrome extension minimal floating UI instead of the full panel.',
  learn:
    'Legacy per-section learn (/v1/template/learnSections). vs learn_plus: whole-note async learn. vs signed_note_learner: learns from signed-off notes.',
  ai_chat_model: 'LLM used for note generation / chat for this user.',
  ai_score_model: 'LLM used to grade notes (compliance / scorecard).',
  ai_transcribe_model: 'Speech-to-text model used for recordings.',
  show_coding_tab: 'Shows the Coding tab on the visit page.',
  beta_prompts:
    'Uses KV prompt overrides (Instruction Assistant Prompts) instead of the built-in prompts.',
  always_use_previous_note:
    'Always feeds the previous note into generation, skipping the AI check. vs prior_note_contract: controls how prior-note values are formatted, not whether they are used. vs longitudinal_context: several prior visits, not just the last note.',
  accordion_compliance:
    'Confluent only: grades notes against accordion compliance rules. vs compliance_sign_off_gate: blocks sign-off on a failing grade. vs scorecard: relabels compliance as conversion coaching.',
  prior_note_contract:
    'COM-519: stricter prompt contract for carrying values from the prior note. vs always_use_previous_note: that one decides whether the prior note is used.',
  score_all_notes:
    'COM-390: lets admins bulk-grade every clinic note, including ones written without Comprehend. Gates the bulk-trigger button + recent runs panel.',
  patient_notes_chat:
    "COM-434: visit chat reads all of the patient's notes across the sub. vs longitudinal_context: feeds prior visits into note generation, not chat. vs get_all_notes: captures the notes from the EMR.",
  longitudinal_context:
    "Feeds the patient's prior visits into /comprehend and /question. vs patient_notes_chat: chat only. vs always_use_previous_note: last note only.",
  signed_note_learner:
    "Algorithm that learns from signed-off notes: slow_learn (style examples) or agentic_learn (may rewrite the user's template instructions). vs learn / learn_plus: those learn from in-app edits.",
  block_account_sharing:
    'COM-407: one active session per client slot; login asks before signing out the other device.',

  // Frontend (FRONTEND_FEATURES)
  synapse: 'Shows the Synapse voice assistant.',
  show_multi_recorder:
    'Shows the Multi Recorder button on the Visits page. vs multirecord_tray: the newer MultiRecord++ tray. vs comprehend_live: full ambient agent.',
  go_to_emr: 'Shows the EMR sidebar link for EMRs where it is not on by default.',
  sync_cal:
    'Calendar sync (schedule → visits). WebPT needs this on; Stride/Prompt/Raintree are on unless this is set false.',
  get_all_notes:
    "Extension captures the patient's existing EMR notes and uploads them. vs raintree_note_capture: Raintree also needs that flag. vs patient_notes_chat: uses the captured notes in chat.",
  raintree_note_capture:
    'Allows get_all_notes capture on Raintree. No effect unless get_all_notes is on.',
  user_note_rules: "Applies the user's own note rules when generating notes.",
  new_rt_integration:
    'Raintree: new injector. vs raintree_note_capture: note capture only, not injection.',
  force_onboarding_checklist:
    'Shows the onboarding checklist regardless of account age (for old test accounts).',
  new_prompt_integration:
    'Prompt EMR: flex++ "full Prompt" injector — asks the whole note as one nested question instead of ~40 flat ones.',
  cora_rom:
    'CORA: routes through NewCoraHealthInject (ROM WNL/WFL, STG goal mapping).',
  new_webpt_soap2_integration:
    'WebPT SOAP2: new injector. vs webpt2_chrome_freeze_fix: only the Chrome freeze workaround.',
  new_webpt_integration:
    'WebPT 1.0: full-note injector — one Comprehend fills every tab and opens Yes/No-gated fields itself.',
  webpt2_chrome_freeze_fix:
    'WebPT SOAP2: workaround for the Chrome freeze during injection. vs new_webpt_soap2_integration: the injector itself.',
  pte_custom_note_types:
    'PT Everywhere: reads custom note templates (Open Answer and Table sections).',
  comprehend_live:
    'Comprehend Live ambient agent tray. Wins over multirecord_tray if both are on.',
  multirecord_tray:
    'MultiRecord++ V0.5 tray (timeline, auto-switcher, schedule loader), no ambient agent. vs comprehend_live: full ambient; that wins if both on. vs show_multi_recorder: legacy button.',
  multirecord_context_and_error_prevention:
    'Multi-record sliding-window segmentation + attribution review on the Multi Recorder page. Add-on to multirecord_tray.',
  emr_beta: 'Uses beta EMR directives (EMR Directives registry) in prompts.',
  compliance_sign_off_gate:
    'COM-427, Confluent only: blocks sign-off until the compliance grade passes. vs accordion_compliance: grading only, no gate.',
  learn_plus:
    'Learn++: whole-note async learn (/v1/template/learnNote). vs learn: legacy per-section. vs signed_note_learner: learns from signed-off notes.',
  scorecard:
    'Relabels the compliance surface as "Scorecard" and swaps grading to conversion coaching. Off = compliance.',
  replay_force:
    'Records sessions even on slow machines / off-by-default EMRs, for debugging a reported bug. vs session_replay: normal continuous replay.',
  session_replay:
    'Continuous session replay. Unset = ON; set false to stop. vs replay_force: overrides device/EMR limits.',
  dictation: 'Push-to-talk dictation button next to Live Visit.',
  patient_match_warning:
    'Warns when the chart open in the EMR is a different patient than the visit.',
  vendor_apps: 'Apps tab + iframe host and Admin Settings → Apps registration.',
  comprehend_feedback: 'Thumbs up/down bar after each Comprehend click.',
  zendesk_support_chat: 'Support Chat opens Zendesk instead of Crisp.',
};

export const FeatureInfo = ({ featureName, idPrefix = 'ff' }) => {
  const description = FEATURE_DESCRIPTIONS[featureName];
  if (!description) return null;
  const id = `${idPrefix}-info-${featureName}`;
  return (
    <>
      <i
        id={id}
        className="fa fa-info-circle"
        style={{ marginLeft: '6px', color: '#8898aa', cursor: 'pointer' }}
      />
      <UncontrolledTooltip target={id}>{description}</UncontrolledTooltip>
    </>
  );
};
